import React from "react";
import { Zap, Clock, ShieldCheck } from "lucide-react";
import { FormRunner } from "@/components/forms/FormRunner";
import { US_STATES } from "@/data/site";

export default function EMDFunding() {
  const fields = [
    { name: "applicant_name", label: "Applicant Name", required: true },
    { name: "company", label: "Company" },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "phone", label: "Phone" },
    { name: "property_address", label: "Property Address", full: true },
    { name: "property_state", label: "Property State", type: "select", options: US_STATES },
    { name: "purchase_price", label: "Purchase Price", type: "number" },
    { name: "emd_amount", label: "EMD Amount Needed", type: "number", required: true },
    { name: "emd_due_date", label: "EMD Due Date", type: "date" },
    { name: "closing_date", label: "Closing Date", type: "date" },
    { name: "buyer_entity", label: "Buyer Entity" },
    { name: "seller", label: "Seller" },
    { name: "title_company", label: "Title Company" },
    { name: "is_assignment", label: "Is This an Assignment?", type: "select", options: ["Yes", "No"] },
    { name: "is_double_close", label: "Is This a Double Close?", type: "select", options: ["Yes", "No"] },
    { name: "assignment_fee", label: "Expected Assignment Fee / Gross Profit", type: "number" },
    { name: "notes", label: "Notes", type: "textarea" },
  ];
  const uploads = [
    { key: "pa", label: "Upload Purchase Agreement", category: "Purchase Agreement" },
    { key: "add", label: "Upload Additional Documents", category: "Other" },
  ];

  return (
    <div>
      <section className="relative border-b border-edge overflow-hidden">
        <div className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-gold/10 blur-[120px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/5 px-4 py-1.5 mb-5">
            <Zap className="w-4 h-4 text-gold" /><span className="eyebrow">Earnest Money Deposit Funding</span>
          </div>
          <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl">Secure the deal without tying up your capital.</h1>
          <p className="mt-5 text-base sm:text-lg text-dim max-w-2xl leading-relaxed">
            Short-term capital for qualified real estate investment transactions requiring an Earnest Money Deposit (EMD).
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
            {[
              { icon: Clock, t: "Time-sensitive ready", d: "Built for purchase agreements and simultaneous contracts." },
              { icon: ShieldCheck, t: "Qualified transactions", d: "For wholesalers, investors, and acquisition teams." },
              { icon: Zap, t: "Simple request", d: "A short application — attach your purchase agreement." },
            ].map((x, i) => (
              <div key={i} className="group card-rich p-5" data-testid={`emd-feature-${i}`}>
                <div className="w-10 h-10 icon-chip"><x.icon className="w-5 h-5 text-gold" strokeWidth={1.7} /></div>
                <div className="mt-3 text-sm font-bold text-white">{x.t}</div>
                <div className="text-xs text-dim mt-1">{x.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="mb-8">
          <div className="eyebrow mb-2">Get EMD Funding</div>
          <h2 className="h-display text-2xl sm:text-3xl text-white">EMD Funding Request</h2>
          <p className="mt-2 text-sm text-dim">Ideal for wholesalers, investors, acquisition teams, high-volume operators, and real estate investment companies.</p>
        </div>
        <FormRunner
          type="emd"
          fields={fields}
          uploads={uploads}
          submitLabel="Submit EMD Request"
          testIdPrefix="emd"
          successMessage="Your EMD funding request has been submitted. A BullsEye Capital representative will review the transaction and contact you regarding next steps."
        />
      </section>
    </div>
  );
}
